/**
 * Shared cart.js line-item pricing for drawer rows + bill summary.
 *
 * Reward-bar order discounts often leave final_line_price at the pre-discount
 * total while line_price / discounts[] / discounted_price carry the reduction.
 */

function sumDiscountRows(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce(
    (sum, row) => sum + Math.abs(Number(row?.amount) || 0),
    0
  );
}

/**
 * Resolve the post-discount line total (cents) from Shopify cart.js fields.
 * @param {object} item
 * @param {number} originalSellingLineCents - original_line_price or price * qty
 */
export function resolveCartLineDiscountedTotalCents(item, originalSellingLineCents) {
  const qty = Math.max(1, Number(item?.quantity) || 1);
  const original = Math.max(0, Number(originalSellingLineCents) || 0);
  if (!original) return 0;

  const candidates = [];

  const linePrice = Number(item?.line_price);
  if (Number.isFinite(linePrice) && linePrice >= 0 && linePrice < original) {
    candidates.push(linePrice);
  }

  const finalLinePrice = Number(item?.final_line_price);
  if (
    Number.isFinite(finalLinePrice) &&
    finalLinePrice >= 0 &&
    finalLinePrice < original
  ) {
    candidates.push(finalLinePrice);
  }

  const unitPrice = Number(item?.price ?? item?.original_price);
  const discountedUnit = Number(item?.discounted_price);
  if (
    Number.isFinite(discountedUnit) &&
    discountedUnit >= 0 &&
    Number.isFinite(unitPrice) &&
    unitPrice > 0 &&
    discountedUnit < unitPrice
  ) {
    candidates.push(discountedUnit * qty);
  }

  const discountCents = Math.max(
    sumDiscountRows(item?.discounts),
    Number(item?.total_discount) || 0,
    Number(item?.line_level_total_discount) || 0,
    sumDiscountRows(item?.line_level_discount_allocations)
  );
  if (discountCents > 0) {
    candidates.push(Math.max(0, original - discountCents));
  }

  if (candidates.length) {
    return Math.min(...candidates);
  }

  if (Number.isFinite(finalLinePrice) && finalLinePrice >= 0) {
    return finalLinePrice;
  }
  if (Number.isFinite(linePrice) && linePrice >= 0) {
    return linePrice;
  }
  return original;
}

/**
 * @returns {{
 *   finalLine: number;
 *   originalLine: number;
 *   originalFromDiscounts: number;
 *   compareAtLine: number;
 *   savings: number;
 *   showDiscount: boolean;
 *   percentOff: number;
 * }}
 */
export function getCartLineItemPricing(item) {
  const qty = Math.max(1, Number(item?.quantity) || 1);
  const unitPrice = Number(item?.price ?? item?.original_price ?? 0);
  const originalLineField = Number(item?.original_line_price);
  const originalFromDiscounts =
    Number.isFinite(originalLineField) && originalLineField > 0
      ? originalLineField
      : unitPrice > 0
        ? unitPrice * qty
        : 0;

  const unitCompareAt = Number(
    item?._weu_compare_at_price ??
      item?.compare_at_price ??
      item?.compareAtPrice ??
      0
  );
  const compareAtLine =
    Number(item?._weu_compare_at_line) > 0
      ? Number(item._weu_compare_at_line)
      : unitCompareAt > 0
        ? unitCompareAt * qty
        : 0;

  const finalLine = resolveCartLineDiscountedTotalCents(
    item,
    originalFromDiscounts
  );

  const originalLine = Math.max(
    originalFromDiscounts,
    compareAtLine,
    Number.isFinite(finalLine) ? finalLine : 0
  );

  const showDiscount = originalLine > finalLine;
  const savings = showDiscount ? Math.max(0, originalLine - finalLine) : 0;
  // Badge % OFF from catalog MRP (compare-at vs selling) — not after cart/order
  // coupons, which inflated badges to ~83% when Offer discount was 0%.
  let percentOff = 0;
  if (compareAtLine > originalFromDiscounts && compareAtLine > 0) {
    percentOff = Math.max(
      1,
      Math.round(
        ((compareAtLine - originalFromDiscounts) / compareAtLine) * 100
      )
    );
  } else if (originalFromDiscounts > finalLine && originalFromDiscounts > 0) {
    percentOff = Math.max(
      1,
      Math.round(
        ((originalFromDiscounts - finalLine) / originalFromDiscounts) * 100
      )
    );
  }

  return {
    finalLine,
    originalLine,
    originalFromDiscounts,
    compareAtLine,
    savings,
    showDiscount,
    percentOff,
  };
}
